/**
 * IntelligenceInfographic — visual "front-matter" for an Intelligence report.
 * Sits between the cover header and the chart suite. Surfaces big-number
 * callouts + iconography that an analyst can screenshot or that the PDF
 * exporter captures alongside the rest of the report.
 *
 * Driven entirely by `charts.universal` + a few computed signals from the
 * underlying chart payload — no extra backend call needed.
 */
import React from "react";
import {
  ChartLineUp, Users, MapPin, GenderIntersex, Sparkle, Compass, Pulse, Scales,
} from "@phosphor-icons/react";

const fmtN = (n) => {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
};

function StatTile({ icon: Icon, label, value, sub, accent = "#16A34A" }) {
  return (
    <div className="border-2 border-black bg-white p-4 flex flex-col gap-1 relative overflow-hidden">
      <div className="absolute -top-3 -right-3 opacity-5">
        <Icon size={88} weight="fill" />
      </div>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-7 h-7" style={{ background: accent }}>
          <Icon size={16} weight="bold" color="#fff" />
        </span>
        <div className="overline text-[10px] text-neutral-500">{label}</div>
      </div>
      <div className="font-black text-3xl tabular-nums tracking-tighter leading-none mt-1">{value}</div>
      {sub && <div className="text-[11px] text-neutral-500 mt-0.5 leading-snug">{sub}</div>}
    </div>
  );
}

function HeadlineCard({ scope, charts, reportTypeLabel }) {
  // Compute a "leading metric" depending on scope.
  let leadLabel = "Headline finding";
  let leadValue = "—";
  let leadSub = "";
  if (scope === "tracker") {
    const latest = charts?.latest_snapshot || [];
    const top = [...latest].sort((a, b) => (b.pct || 0) - (a.pct || 0))[0];
    if (top) {
      leadLabel = "Latest leading answer";
      leadValue = `${top.pct}%`;
      leadSub = `said "${top.option}" in the most recent wave`;
    }
    // Movement
    const delta = charts?.delta || [];
    const mover = [...delta].sort((a, b) => Math.abs(b.change) - Math.abs(a.change))[0];
    if (mover && mover.change !== 0) {
      leadSub += ` · biggest mover: ${mover.option} ${mover.change > 0 ? "+" : ""}${mover.change}pp`;
    }
  } else {
    const grid = charts?.question_grid || [];
    const tracker = grid.find((q) => q.is_tracker) || grid[0];
    if (tracker?.data?.length) {
      const top = tracker.data[0];
      leadLabel = "Most chosen answer";
      leadValue = `${top.pct}%`;
      leadSub = `chose "${top.option}" — ${tracker.question.slice(0, 90)}${tracker.question.length > 90 ? "…" : ""}`;
    }
  }
  return (
    <div className="bg-black text-white p-5 relative overflow-hidden border-2 border-black">
      <div className="absolute -bottom-6 -right-6 opacity-10">
        <Sparkle size={140} weight="fill" color="#16A34A" />
      </div>
      <div className="overline text-mango text-[10px] mb-2">{reportTypeLabel || "Intelligence Brief"} · headline</div>
      <div className="font-black text-5xl sm:text-6xl tabular-nums tracking-tighter leading-none" style={{ color: "#16A34A" }}>
        {leadValue}
      </div>
      <div className="text-sm mt-2 text-white/85 leading-snug max-w-xl">{leadSub || leadLabel}</div>
    </div>
  );
}

export default function IntelligenceInfographic({ scope, charts, reportTypeLabel }) {
  if (!charts || !charts.universal) return null;
  const u = charts.universal;
  const genderTop = Object.entries(u.gender_split || {}).sort((a, b) => b[1] - a[1])[0];
  const regionTop = Object.entries(u.region_split || {}).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2 bg-gradient-to-b from-neutral-50 via-white to-white border-b border-neutral-200" data-testid="intel-infographic">
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <HeadlineCard scope={scope} charts={charts} reportTypeLabel={reportTypeLabel} />
        <div className="grid grid-cols-2 gap-3">
          <StatTile
            icon={Users}
            label="Sample size"
            value={fmtN(u.total_n)}
            sub={scope === "tracker" ? `across ${u.waves_count} waves` : "respondents in this wave"}
            accent="#16A34A"
          />
          <StatTile
            icon={scope === "tracker" ? ChartLineUp : Pulse}
            label={scope === "tracker" ? "Waves analysed" : "Latest wave n"}
            value={scope === "tracker" ? u.waves_count : fmtN(u.latest_n)}
            sub={scope === "tracker" ? "longitudinal series" : "single-wave snapshot"}
            accent="#F59E0B"
          />
          <StatTile
            icon={MapPin}
            label="Top region"
            value={regionTop ? `${regionTop[1]}%` : "—"}
            sub={regionTop ? regionTop[0] : "no region data"}
            accent="#0EA5E9"
          />
          <StatTile
            icon={GenderIntersex}
            label="Gender mix"
            value={genderTop ? `${genderTop[1]}%` : "—"}
            sub={genderTop ? `${genderTop[0]} share` : "no gender data"}
            accent="#7C3AED"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap text-[10px] text-neutral-600">
        <span className="inline-flex items-center gap-1 px-2 py-1 border border-neutral-300 bg-white">
          <Compass size={11} weight="bold" /> Scope: {scope === "tracker" ? "Longitudinal tracker" : "Single wave"}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-1 border border-neutral-300 bg-white">
          <Scales size={11} weight="bold" /> Numbers are exact aggregates · narrative is AI-generated
        </span>
      </div>
    </div>
  );
}
